import { keyframes } from '@emotion/css'
import styled from '@emotion/styled'
import { colors } from '../../__styling/settings/colors'
import { breakpoint } from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { themedValue } from '../../__styling/themed-value'
import {
    TheNightlyThemeConfig,
    Theme,
    getComponentTheme,
    metrics,
} from '../../__styling'
import { textMixin } from '../../mixins/TextMixin/TextMixin'
import { IconSpinner } from '../../icons'

export const StyledHeading = styled('h1')(({ theme }) => ({
    width: '100%',
    fontSize: calcRem(22),
    lineHeight: 1.15,
    margin: `0 0 ${calcRem(theme.margins.md)} 0`,
    padding: 0,

    [breakpoint('sm')]: {
        fontSize: calcRem(32),
    },
}))
StyledHeading.displayName = 'SearchHeading'

const getHeadingStyle = (theme: Theme) => [textMixin(theme, 'article')]

export const StyledArticleMessage = styled('p')(({ theme }) => [
    getHeadingStyle(theme),
    {
        margin:
            theme.kind === 'thenightly'
                ? calcRem(
                      metrics.thenightly.margins.md,
                      metrics.thenightly.margins.lg,
                  )
                : calcRem(metrics.thenightly.margins.xxxl, 0),

        [breakpoint('lg')]: {
            margin:
                theme.kind === 'thenightly'
                    ? calcRem(metrics.thenightly.margins.xxxl, 0)
                    : undefined,
        },
    },
])
StyledArticleMessage.displayName = 'StyledArticleMessage'

export const StyledMessage = styled('p')(({ theme }) => ({
    width: '100%',
    fontSize: calcRem(22),
    fontWeight: 700,
    lineHeight: 1.2,
    margin: calcRem(theme.margins.md, 0),

    marginLeft:
        theme.kind === 'sevennews' ? calcRem(theme.margins.sm) : undefined,
    marginRight:
        theme.kind === 'sevennews' ? calcRem(theme.margins.sm) : undefined,

    [breakpoint('sm')]: {
        fontSize: calcRem(32),

        marginLeft: theme.kind === 'sevennews' ? 0 : undefined,
        marginRight: theme.kind === 'sevennews' ? 0 : undefined,
    },
}))
StyledMessage.displayName = 'SearchMessage'

export const StyledSearchTerm = styled('span')(({ theme }) => ({
    fontWeight: 400,
    fontStyle: 'italic',
    color: themedValue(theme, {
        thewest: colors.thewest.greyElephant,
        sevennews: colors.sevennews.red,
        perthnow: colors.perthnow.greyMarble,
        fallback: undefined,
    }),
}))
StyledSearchTerm.displayName = 'SearchTerm'

export const StyledSearchResults = styled('div')(({ theme }) => ({
    margin:
        theme.kind === 'thenightly'
            ? `auto ${calcRem(theme.margins.outerMargin)}`
            : 'auto',

    ...(theme.kind === 'sevennews' && {
        marginTop: calcRem(metrics.sevennews.margins.md),
    }),

    [breakpoint('lg')]: {
        ...(theme.kind !== 'sevennews' && {
            margin: 'auto',
        }),
    },
}))
StyledSearchResults.displayName = 'StyledSearchResults'

export const StyledTitle = styled('p')(
    ({ theme }) => textMixin<TheNightlyThemeConfig>(theme, 'article-main'),
    ({ theme }) => ({
        display: 'block',
        width: '100%',
        margin:
            theme.kind === 'sevennews'
                ? calcRem(
                      metrics.sevennews.margins.sm,
                      metrics.sevennews.margins.lg,
                  )
                : calcRem(
                      metrics.thenightly.margins.sm,
                      metrics.thenightly.margins.lg,
                  ),

        [breakpoint('lg')]: {
            display: 'none',
        },
    }),
)
StyledTitle.displayName = 'StyledTitle'

export const StyledSpinnerContainer = styled('div')(({ theme }) => ({
    margin: '20% auto',
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: calcRem(theme.margins.md),
    alignItems: 'center',
}))

const spin = keyframes({
    to: {
        transform: 'rotate(360deg)',
    },
})

export const StyledIconSpinner = styled(IconSpinner)({
    display: 'block',
    animation: `${spin} 0.6s linear infinite`,
})
